import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import {
    andAllWhere,
    andWhere,
    buildQuery,
    noop,
    Ops,
    orderBy,
    OrderVariant,
    orWhere,
    page,
    select,
    where,

} from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { HttpClient } from "@angular/common/http";
import { AbstractService } from "app/services/abstract.service";
import { environment as env } from "../../../../../../environments/environment.prod";
import { MaterialStockAttributes } from "@albatrosdeveloper/ave-models-package";
import { IPagination, IPaginationResult, IPaginationResult2 } from "app/common/interfaces";
import { IFilters } from "app/modules/components/filters/interfaces/filterInterface";

const apiUrl = env.API_MATERIAL_URL;
const moduleName = "material_stock";

@Injectable({
    providedIn: "root",
})
export class AttentionStockService extends AbstractService<MaterialStockAttributes, MaterialStockAttributes, MaterialStockAttributes> {
    constructor(
        http: HttpClient,
    ) {
        super(apiUrl, moduleName, http);
    }

    /**
     * Pagination
     * @param options Pagination options
     * @returns Paginated data
     */
    findAllWithCount(
        options: IPagination<MaterialStockAttributes>,
        warehouseIds: string[]
    ): Observable<IPaginationResult<MaterialStockAttributes>> {
        const query = buildQuery<MaterialStockAttributes>(
            where('_deleted', Ops.eq(false)),
            andWhere("materialId", Ops.like(options.search ?? "", "i")),
            orWhere("unitOfMeasureId", Ops.like(options.search ?? "", "i")),
            warehouseIds && warehouseIds.length > 0 ? andAllWhere('warehouseId' as keyof MaterialStockAttributes, Ops.in(...warehouseIds)) : noop(),

            orderBy(
                options.sort ?? "materialId",
                options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
            ),
            page(options.currentPage ?? 1, options.limitPerPage ?? 100)
        );

        const API_URL: string =
            apiUrl +
            `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
        return this.http.get<IPaginationResult<MaterialStockAttributes>>(API_URL);
    }

    getAllPaginateStock(
        options: IPagination<MaterialStockAttributes>,
        dataFilter: IFilters
    ): Observable<IPaginationResult2<MaterialStockAttributes>> {

        let query = {
            "$and": []
        };

        if (dataFilter.warehouseId && dataFilter.warehouseId.length > 0) {
            query["$and"].push({
                "warehouseId": { "$in": dataFilter.warehouseId }
            });
        }

        if (dataFilter.companyId) {
            query["$and"].push({
                "companyId": { "$eq": dataFilter.companyId }
            });
        }

        if (dataFilter.pickerDue_start && dataFilter.pickerDue_end) {
            query["$and"].push({
                "pickerDue_start": { "$gte": dataFilter.pickerDue_start, "$lte": dataFilter.pickerDue_end }
            });
        }

        if (dataFilter.salesOfficeId && dataFilter.salesOfficeId.length > 0) {
            query["$and"].push({
                "pickerDue_end": { "$in": dataFilter.salesOfficeId }
            });
        }

        if (dataFilter.materialId && dataFilter.materialId.length > 0) {
            query["$and"].push({
                "warehouseId": { "$in": dataFilter.materialId }
            });
        }

        if (dataFilter.materialId && dataFilter.materialId.length > 0) {
            query["$and"].push({
                "materialId": { "$in": dataFilter.materialId }
            });
        }

        if (dataFilter.materialGroupId && dataFilter.materialGroupId.length > 0) {
            query["$and"].push({
                "materialGroupId": { "$in": dataFilter.materialGroupId }
            });
        }

        if (dataFilter.materialBrandId) {
            query["$and"].push({
                "materialBrandId": { "$in": dataFilter.materialBrandId }
            });
        }

        if (options.search) {
            query["$and"].push({
                "$or": [
                    {
                        "warehouseDetails.name": {
                            "$regex": options.search,
                            "$options": "i"
                        }
                    },
                    {
                        "warehouseDetails.externalKeys.code": {
                            "$regex": options.search,
                            "$options": "i"
                        }
                    },
                    {
                        "materialDetails.name": {
                            "$regex": options.search,
                            "$options": "i"
                        }
                    },
                    {
                        "materialDetails.externalKeys.code": {
                            "$regex": options.search,
                            "$options": "i"
                        }
                    },
                    {
                        "lot": {
                            "$regex": options.search,
                            "$options": "i"
                        }
                    },
                    {
                        "unitOfMeasureDetails.name": {
                            "$regex": options.search,
                            "$options": "i"
                        }
                    },
                    {
                        "unitOfMeasureDetails.externalKeys.code": {
                            "$regex": options.search,
                            "$options": "i"
                        }
                    }
                ]
            });
        }

        if (query["$and"].length === 0) {
            query["$and"].push({});
        }

        const API_URL: string = apiUrl +
            `api/${moduleName}/paginated?page=${options.currentPage ?? 1}&limit=${options.limitPerPage ?? 100}&sort=${options.sort ?? "materialId"}:${options.sortDirection == "ASC" ? 1 : -1}`;
        return this.http.post<IPaginationResult2<MaterialStockAttributes>>(API_URL, query);
    }

    //V2
    getAllPaginate(
        options: IPagination<MaterialStockAttributes>,
        warehouseIds: string[],
        materialId?: string[],
        pickerDue_start?: Date,
        pickerDue_end?: Date
    ): Observable<IPaginationResult2<MaterialStockAttributes>> {
        let query = {
            "$and": []
        };

        if (warehouseIds && warehouseIds.length > 0) {
            query["$and"].push({
                "warehouseId": { "$in": warehouseIds }
            });
        }

        if (materialId && materialId.length > 0) {
            query["$and"].push({
                "materialId": { "$in": materialId }
            });
        }

        if (pickerDue_start && pickerDue_end) {
            query["$and"].push({
                lotExpirationDate: {
                    $gte: pickerDue_start,
                    $lte: pickerDue_end
                }
            });
        } else if (pickerDue_start) {
            query["$and"].push({ lotExpirationDate: { $gte: pickerDue_start } });
        } else if (pickerDue_end) {
            query["$and"].push({ lotExpirationDate: { $lte: pickerDue_end } });
        }

        if (options.search) {
            query["$and"].push({
                "$or": [
                    {
                        "warehouseDetails.name": {
                            "$regex": options.search,
                            "$options": "i"
                        }
                    },
                    {
                        "warehouseDetails.externalKeys.code": {
                            "$regex": options.search,
                            "$options": "i"
                        }
                    },
                    {
                        "materialDetails.name": {
                            "$regex": options.search,
                            "$options": "i"
                        }
                    },
                    {
                        "materialDetails.externalKeys.code": {
                            "$regex": options.search,
                            "$options": "i"
                        }
                    },
                    {
                        "lot": {
                            "$regex": options.search,
                            "$options": "i"
                        }
                    },
                    {
                        "unitOfMeasureDetails.name": {
                            "$regex": options.search,
                            "$options": "i"
                        }
                    },
                    {
                        "unitOfMeasureDetails.externalKeys.code": {
                            "$regex": options.search,
                            "$options": "i"
                        }
                    }
                ]
            });
        }

        if (query["$and"].length === 0) {
            query["$and"].push({});
        }

        const API_URL: string = apiUrl +
            `api/${moduleName}/paginated?page=${options.currentPage ?? 1}&limit=${options.limitPerPage ?? 100}&sort=${options.sort ?? "materialId"}:${options.sortDirection == "ASC" ? 1 : -1}`;
        return this.http.post<IPaginationResult2<MaterialStockAttributes>>(API_URL, query);
    }


    getFileTemplateStock(): string {
        return env.STOCK_LOAD_TEMPLATE || null;
    }

    importFile(data: FormData) {
        const API_URL: string = apiUrl + `api/${moduleName}/upload`;
        return this.http.post(API_URL, data);
    }
}
